import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { api } from "@/lib/api";
import { useApiMutation } from "@/lib/use-api-mutation";
import { createFileRoute } from "@tanstack/react-router";
import { SquareTerminal } from "lucide-react";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

/**
 * `/connect/hub?code=…`: a terminal running `lair setup` asks to act
 * as you. The code here must be the one it shows; allowing mints a token with
 * only the scopes listed, which the terminal collects once.
 */

type ConnectRequest = {
  code: string;
  host: string;
  scopes: Array<{ key: string; label: string }>;
  skillScopes?: Array<{ key: string; label: string }>;
};

export const Route = createFileRoute("/_app/connect/hub")({
  validateSearch: (search: Record<string, unknown>): { code?: string } =>
    typeof search.code === "string" ? { code: search.code } : {},
  loaderDeps: ({ search }) => ({ code: search.code }),
  loader: ({ deps }) =>
    deps.code
      ? api
          .get<ConnectRequest>(`/api/agent-connect/request/${encodeURIComponent(deps.code)}`)
          .catch(() => null)
      : null,
  component: ConnectHubAgentPage,
});

function ConnectHubAgentPage() {
  const { t } = useTranslation();
  const request = Route.useLoaderData();
  const [answer, setAnswer] = useState<"allowed" | "denied" | null>(null);
  const respond = useApiMutation(
    (allow: boolean) => api.post(`/api/agent-connect/request/${request?.code ?? ""}`, { allow }),
    { fallbackMessage: t("miscConnect.answerFailed") },
  );

  const decide = async (allow: boolean) => {
    if ((await respond.mutate(allow)) !== undefined) setAnswer(allow ? "allowed" : "denied");
  };

  return (
    <div className="mx-auto max-w-lg py-10">
      <Card data-testid="connect-hub">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <SquareTerminal className="h-5 w-5" />
            {t("miscConnect.title")}
          </CardTitle>
          <CardDescription>{t("miscConnect.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {answer === "allowed" ? (
            <p className="text-sm">{t("miscConnect.allowed")}</p>
          ) : answer === "denied" ? (
            <p className="text-sm">{t("miscConnect.denied")}</p>
          ) : !request ? (
            <p className="text-sm text-muted-foreground">
              <Trans
                i18nKey="miscConnect.expired"
                components={{ code: <code className="rounded bg-muted px-1" /> }}
              />
            </p>
          ) : (
            <>
              <div className="rounded-lg border border-border p-3 text-center">
                <p className="text-xs text-muted-foreground">{t("miscConnect.code")}</p>
                <p className="font-mono text-2xl tracking-widest" data-testid="connect-code">
                  {request.code}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t("miscConnect.onHost", { host: request.host })}
                </p>
              </div>
              {request.scopes.length > 0 && (
                <ScopeList title={t("miscConnect.ableTo")} scopes={request.scopes} />
              )}
              {request.skillScopes?.length ? (
                <ScopeList title={t("miscConnect.skillsAbleTo")} scopes={request.skillScopes} />
              ) : null}
              <p className="text-xs text-muted-foreground">{t("miscConnect.warning")}</p>
              <div className="flex gap-2">
                <Button onClick={() => void decide(true)} disabled={respond.pending}>
                  {t("miscConnect.allow")}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => void decide(false)}
                  disabled={respond.pending}
                >
                  {t("miscConnect.deny")}
                </Button>
              </div>
              {respond.error && <p className="text-sm text-destructive">{respond.error}</p>}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ScopeList({
  title,
  scopes,
}: {
  title: string;
  scopes: Array<{ key: string; label: string }>;
}) {
  return (
    <div className="text-sm">
      <p className="font-medium">{title}</p>
      <ul className="list-disc pl-5 text-muted-foreground">
        {scopes.map((s) => (
          <li key={s.key}>{s.label}</li>
        ))}
      </ul>
    </div>
  );
}
